// Mobile soft-keyboard primer.
//
// Mobile browsers (iOS Safari especially) only raise the on-screen keyboard
// when an input is focused synchronously inside a user gesture (the tap that
// triggered the handler). Our add-* flows do `await createTask/createItem/
// createItemCounter` BEFORE opening the edit modal, so by the time the
// modal's first <input> is focused (via autoFocus / onEntered) the gesture
// is gone and the keyboard stays down until the user manually taps.
//
// Workaround: synchronously focus a hidden but focusable <input> at the very
// start of the click handler (still inside the gesture). The OS opens the
// keyboard for that primer input, and when we later focus() the real input
// after the modal's onEntered fires, the keyboard hands off without closing.
//
// The primer is created once, lazily, and reused for the life of the page.
// It must NOT be display:none / visibility:hidden -- those are unfocusable.

const PRIMER_ID = 'taskpogo-keyboard-primer';

function getPrimer() {
  if (typeof document === 'undefined') return null;

  let primer = document.getElementById(PRIMER_ID);
  if (primer) return primer;

  primer = document.createElement('input');
  primer.id = PRIMER_ID;
  primer.type = 'text';
  primer.setAttribute('aria-hidden', 'true');
  primer.setAttribute('tabindex', '-1');
  primer.setAttribute('autocomplete', 'off');
  primer.readOnly = true;
  // Visually hidden but still focusable. font-size:16px avoids iOS auto-zoom
  // on focus; opacity:0 + 1px size keeps it out of layout/visual flow.
  Object.assign(primer.style, {
    position: 'fixed',
    bottom: '0',
    left: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    padding: '0',
    border: '0',
    fontSize: '16px',
    pointerEvents: 'none',
  });
  document.body.appendChild(primer);
  return primer;
}

// Focus a hidden input synchronously to open (and hold) the mobile keyboard
// across an async gap before the real edit modal mounts. Safe to call in
// non-browser environments (no-op).
export function primeKeyboard() {
  const primer = getPrimer();
  if (!primer) return;
  try {
    primer.focus({ preventScroll: true });
  } catch {
    primer.focus();
  }
}

// Blur the primer if it currently has focus. Used in error paths so a failed
// create doesn't leave the keyboard up over the un-changed page.
export function blurPrimer() {
  if (typeof document === 'undefined') return;
  const primer = document.getElementById(PRIMER_ID);
  if (primer && document.activeElement === primer) {
    primer.blur();
  }
}

// Test-only: tear down the primer element. Not part of the public surface.
export function __resetKeyboardPrimerForTests() {
  if (typeof document === 'undefined') return;
  const primer = document.getElementById(PRIMER_ID);
  if (primer && primer.parentNode) {
    primer.parentNode.removeChild(primer);
  }
}
